// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { Check, Close } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, Card, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { EligibleInstrument } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Types/module';
import { useGetAllInstrumentConfigurations } from '../../../hooks/queries/useGetAllInstrumentConfigurations';

interface InstrumentMultiSelectProps {
  onRemove: (inst: EligibleInstrument) => void;
  onClick: (inst: EligibleInstrument) => void;
  selectedInstruments: EligibleInstrument[];
}
export const InstrumentMultiSelect: React.FC<InstrumentMultiSelectProps> = ({
  onClick,
  selectedInstruments,
  onRemove,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const isOpen = Boolean(anchorEl);

  const handleClosePopover = () => {
    setAnchorEl(null);
  };
  const allInstConfigurations = useGetAllInstrumentConfigurations().data;

  return (
    <Box>
      <Box
        sx={{
          cursor: 'pointer',
          p: 1,
          mb: 2,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
        component={Card}
        variant="outlined"
        onClick={handleClick}
      >
        <Typography variant="body1" color="GrayText">
          {selectedInstruments.length > 0
            ? selectedInstruments.map((s) => s.instrument.id).join(', ')
            : 'Select from listed assets'}
        </Typography>
        <Box sx={{ ml: 'auto', alignItems: 'center', display: 'flex' }}>
          <ArrowDropDownIcon color="action" />
        </Box>
      </Box>
      <Menu
        id="simple-popover"
        open={isOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={handleClosePopover}
      >
        {allInstConfigurations?.instrumentConfigurations?.map((a) => {
          const isSelected =
            selectedInstruments.filter(
              (s) =>
                s.instrument.id === a.payload.defaultIdentifier.id &&
                s.instrument.admin === a.payload.defaultIdentifier.source,
            ).length > 0;
          return (
            <MenuItem
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderLeft: '1px',
                background: isSelected ? '#E7EDFD' : undefined,
              }}
              onClick={() => {
                onClick({
                  instrument: {
                    id: a.payload.defaultIdentifier.id,
                    admin: a.payload.defaultIdentifier.source,
                  },
                });
              }}
              key={a.contract.contractId}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ maxWidth: 550, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">{a.payload.defaultIdentifier.id}</Typography>
                  <Typography
                    sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    variant="caption"
                  >
                    {a.payload.defaultIdentifier.source}
                  </Typography>
                </Box>
                <Check sx={{ visibility: isSelected ? undefined : 'hidden' }} />
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
      {selectedInstruments.map((inst) => (
        <Box
          sx={{
            alignItems: 'center',
            p: 1,
            display: 'flex',
            flexDirection: 'row',
          }}
          key={inst.instrument.admin.concat(inst.instrument.id)}
        >
          <Box
            sx={{
              flexDirection: 'column',
              display: 'flex',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography sx={{ textOverflow: 'ellipsis' }} variant="body2">
              {inst.instrument.id}
            </Typography>
            <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} variant="caption">
              {inst.instrument.admin}
            </Typography>
          </Box>
          <Box>
            <IconButton size="small" color="error" onClick={() => onRemove(inst)}>
              <Close color="error" fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
